import React, { useContext, useState } from "react";
import PropTypes from "prop-types";
import { Button, DateInput } from "@narmi/design_system";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useLocalization } from "@fluent/react";
import Feature from "byzantine/src/Feature";
import { ContextForm, useNotificationContext } from "cerulean";
import utils from "../../utils";
import AccountContext from "../contexts/AccountContext";
import UserFeaturesContext from "../contexts/UserFeaturesContext";
import PayeeSelector from "./PayeeSelector";
import AmountTextInput from "../form/AmountTextInput";
import ComboAccountSelector from "../ComboAccountSelector";
import ActionHeader from "./ActionHeader";
import BillPaySso from "./BillPaySso";

const BillPayForm = ({ onChange, onSubmit, cancel, payees, fetchPayees }) => {
  const { l10n } = useLocalization();
  const features = React.useContext(UserFeaturesContext);
  const [minDate, setMinDate] = useState(DateTime.now().toFormat("MM/dd/yyyy"));
  const [disabledDates, setDisabledDates] = useState([]);
  const { sendNotification } = useNotificationContext();
  const { accounts } = useContext(AccountContext);
  const { t } = useTranslation();

  const updatePayees = (payee) => {
    fetchPayees(payee);
    onChange({ payee_id: payee.id });
    sendNotification({
      type: "success",
      text: l10n.getString("notification-payee-added"),
    });
  };

  const updateDatepickerValue = (date) => {
    onChange({
      payment_date: DateTime.fromFormat(date, "M/d/yyyy").toFormat(
        "MM/dd/yyyy"
      ),
    });
  };

  return (
    <div className="form-card-layout">
      <div className="card">
        <ActionHeader title={t("Make a payment")} icon="dollar-bill-line" />{" "}
        {/* FIXME: migrate as part of <https://linear.app/narmi/issue/NDS-1690/cd-account-detail-fields-translation> */}
        <Feature features={features} or="allow_billpay_from_account">
          <ContextForm.Field required>
            <ComboAccountSelector
              field="from_account_id"
              label={l10n.getString("label-from")}
              showAddExternalAccountLink={false}
              accounts={accounts.filter((a) => a.isValidBillpaySource())}
            />
          </ContextForm.Field>
        </Feature>
        <ContextForm.Field required>
          <PayeeSelector
            field="payee_id"
            label={l10n.getString("label-to")}
            payees={payees}
            updatePayees={updatePayees}
            setMinDate={setMinDate}
            setDisabledDates={setDisabledDates}
            updateDatepickerValue={updateDatepickerValue}
          />
        </ContextForm.Field>
        <ContextForm.Field
          validate={(a) => {
            if (a === "$") return l10n.getString("validation-enter-amount");
            if (utils.parseValueAsFloat(a) <= 0)
              return l10n.getString("form-generic-validation-positive-number");
            return "";
          }}
          required
        >
          <AmountTextInput
            field="amount"
            label={l10n.getString("label-amount")}
          />
        </ContextForm.Field>
        <ContextForm.Field required style={{ marginBottom: "12px" }}>
          <DateInput
            minDate={minDate}
            disableDates={disabledDates}
            dateFormat={"m/d/Y"}
            altInput={true}
            altFormat={"m/d/Y"}
            field="payment_date"
            label={l10n.getString("label-date-mmddyyyy")}
            useIsoOnChange={false}
          />
        </ContextForm.Field>
        <BillPaySso
          label={l10n.getString("label-view-advanced-options")}
          size="s"
        />
        <ContextForm.ActionBar vertical>
          <ContextForm.Action onSubmit={onSubmit} dangerouslyDisableShowLoading>
            <Button
              testId="continue-billpay"
              label={l10n.getString("button-continue")}
            />
          </ContextForm.Action>
          <div className="transfer-cancel-button">
            <Button
              onClick={() => cancel()}
              testId="cancel-billpay-transfer"
              kind="negative"
              type="button"
              label={l10n.getString("button-cancel")}
            />
          </div>
        </ContextForm.ActionBar>
      </div>
    </div>
  );
};

BillPayForm.propTypes = {
  onChange: PropTypes.func,
  onSubmit: PropTypes.func,
  cancel: PropTypes.func,
  payees: PropTypes.array,
  fetchPayees: PropTypes.func,
};

export default BillPayForm;
